import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import palette from 'src/theme/palette';

import {
  formatFinanceValue,
  formatFinancePeriodLabel,
  formatCompareDelta,
  type FinanceMetricFormat,
} from './formatters';
import {
  buildFinanceAdditiveTotals,
  resolveFinanceRowTotal,
} from './financeTotals';

export type FinanceForecastRow = {
  metricKey: string;
  label: string;
  format: string;
  emphasis: string;
  values: number[];
  compareValues?: Array<number | null>;
};

interface ForecastTableProps {
  periods: string[];
  rows: FinanceForecastRow[];
  granularity?: string;
  compareActive?: boolean;
}

const headCellSx = {
  color: palette.text.secondary,
  fontWeight: 700,
  fontSize: '0.8125rem',
  letterSpacing: '0.02em',
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.25,
  whiteSpace: 'nowrap',
  bgcolor: '#f8fafc',
} as const;

const metricCellSx = {
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.1,
  fontSize: '0.8125rem',
  color: palette.text.primary,
  fontWeight: 500,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: palette.common.white,
  minWidth: 220,
  maxWidth: 280,
} as const;

const valueCellSx = {
  borderBottom: `1px solid ${palette.grey[200]}`,
  py: 1.1,
  fontSize: '0.8125rem',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} as const;

const totalHeadCellSx = {
  ...headCellSx,
  fontWeight: 800,
  bgcolor: '#eef2f7',
  borderLeft: `1px solid ${palette.grey[200]}`,
} as const;

const totalValueCellSx = {
  ...valueCellSx,
  fontWeight: 700,
  bgcolor: '#f8fafc',
  borderLeft: `1px solid ${palette.grey[200]}`,
} as const;

function rowStyles(emphasis: string) {
  if (emphasis === 'subtotal') {
    return {
      metric: {
        ...metricCellSx,
        fontWeight: 700,
        bgcolor: '#f4fbf8',
      },
      value: {
        ...valueCellSx,
        fontWeight: 700,
        bgcolor: '#f4fbf8',
      },
    };
  }

  if (emphasis === 'kpi') {
    return {
      metric: {
        ...metricCellSx,
        color: palette.primary.main,
      },
      value: {
        ...valueCellSx,
        color: palette.primary.main,
        fontWeight: 600,
      },
    };
  }

  if (emphasis === 'section') {
    return {
      metric: {
        ...metricCellSx,
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: palette.text.secondary,
        bgcolor: '#eef2f7',
        borderTop: `2px solid ${palette.grey[300]}`,
        py: 1.5,
      },
      value: {
        ...valueCellSx,
        bgcolor: '#eef2f7',
        borderTop: `2px solid ${palette.grey[300]}`,
        py: 1.5,
      },
    };
  }

  if (emphasis === 'spacer') {
    return {
      metric: {
        ...metricCellSx,
        py: 0,
        height: 16,
        border: 0,
        bgcolor: palette.common.white,
      },
      value: {
        ...valueCellSx,
        py: 0,
        height: 16,
        border: 0,
        bgcolor: palette.common.white,
      },
    };
  }

  return {
    metric: metricCellSx,
    value: valueCellSx,
  };
}

export function ForecastTable({
  periods,
  rows,
  granularity,
  compareActive = false,
}: ForecastTableProps) {
  if (!periods.length || !rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
        No finance data available for the selected period.
      </Typography>
    );
  }

  const additiveTotals = buildFinanceAdditiveTotals(rows, 'values');
  const compareAdditiveTotals = compareActive
    ? buildFinanceAdditiveTotals(rows, 'compareValues')
    : null;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderRadius: 2,
          border: '1px solid',
          borderColor: palette.grey[200],
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, pl: 1.5, left: 0, zIndex: 3 }}>
                Metric
              </TableCell>
              {periods.map((period) => (
                <TableCell key={period} align="right" sx={headCellSx}>
                  {formatFinancePeriodLabel(period, granularity)}
                </TableCell>
              ))}
              <TableCell align="right" sx={totalHeadCellSx}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const styles = rowStyles(row.emphasis);
              const colCount = periods.length + 2;
              if (row.emphasis === 'spacer') {
                return (
                  <TableRow key={row.metricKey}>
                    <TableCell
                      colSpan={colCount}
                      sx={{
                        py: 1.25,
                        height: 20,
                        border: 0,
                        bgcolor: palette.common.white,
                      }}
                    />
                  </TableRow>
                );
              }
              if (row.emphasis === 'section') {
                return (
                  <TableRow key={row.metricKey}>
                    <TableCell
                      colSpan={colCount}
                      sx={{ ...styles.metric, pl: 1.5 }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }
              const totalValue = resolveFinanceRowTotal(row, additiveTotals);
              const compareTotal =
                compareActive && compareAdditiveTotals
                  ? resolveFinanceRowTotal(row, compareAdditiveTotals)
                  : null;
              const totalDelta =
                totalValue !== null && compareTotal !== null
                  ? formatCompareDelta(
                      totalValue,
                      compareTotal,
                      row.format as FinanceMetricFormat,
                    )
                  : null;
              const totalDeltaPositive =
                totalDelta != null &&
                !totalDelta.startsWith('-') &&
                totalDelta !== 'New' &&
                !totalDelta.startsWith('+0') &&
                totalDelta !== '0.0%';
              const totalDeltaNegative =
                totalDelta != null && totalDelta.startsWith('-');
              return (
                <TableRow key={row.metricKey} hover>
                  <TableCell sx={{ ...styles.metric, pl: 1.5 }}>
                    {row.label}
                  </TableCell>
                  {row.values.map((value, index) => {
                    const formatted = formatFinanceValue(
                      value,
                      row.format as FinanceMetricFormat,
                    );
                    const compareValue = row.compareValues?.[index] ?? null;
                    const delta =
                      compareActive && compareValue !== null
                        ? formatCompareDelta(
                            value,
                            compareValue,
                            row.format as FinanceMetricFormat,
                          )
                        : null;
                    const isNegative = value < 0;
                    const deltaPositive =
                      delta != null &&
                      !delta.startsWith('-') &&
                      delta !== 'New' &&
                      !delta.startsWith('+0') &&
                      delta !== '0.0%';
                    const deltaNegative =
                      delta != null && delta.startsWith('-');
                    return (
                      <TableCell
                        key={`${row.metricKey}-${periods[index]}`}
                        align="right"
                        sx={{
                          ...styles.value,
                          color: isNegative ? '#c62828' : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.25,
                          }}
                        >
                          <span>{formatted}</span>
                          {delta && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                lineHeight: 1.1,
                                color: deltaNegative
                                  ? '#c62828'
                                  : deltaPositive
                                    ? '#15803d'
                                    : palette.text.secondary,
                              }}
                            >
                              {delta}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      ...styles.value,
                      ...totalValueCellSx,
                      ...(row.emphasis === 'subtotal'
                        ? { bgcolor: '#f4fbf8', fontWeight: 700 }
                        : null),
                      ...(row.emphasis === 'kpi'
                        ? { color: palette.primary.main, fontWeight: 600 }
                        : null),
                      ...(totalValue !== null && totalValue < 0
                        ? { color: '#c62828' }
                        : null),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.25,
                      }}
                    >
                      <span>
                        {totalValue === null
                          ? '—'
                          : formatFinanceValue(
                              totalValue,
                              row.format as FinanceMetricFormat,
                            )}
                      </span>
                      {totalDelta && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            lineHeight: 1.1,
                            color: totalDeltaNegative
                              ? '#c62828'
                              : totalDeltaPositive
                                ? '#15803d'
                                : palette.text.secondary,
                          }}
                        >
                          {totalDelta}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ForecastTable;
